import { ReactNode } from 'react';
import {
  CheckCircleIcon,
  ExclamationCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
} from '@heroicons/react/24/outline';

import { cn } from 'support/helpers/cn/cn';
import { Severity } from 'support/types/ui';
import { WithRef } from 'support/types/withRef';
import { Button } from 'components/Form/Button';
import { LoadingSpinner } from 'components/Loading/LoadingSpinner';

const classes = {
  error: {
    bg: 'bg-error-50',
    iconColor: 'text-error-400',
    text: 'text-error-700',
  },
  warning: {
    bg: 'bg-warning-50',
    iconColor: 'text-warning-500',
    text: 'text-warning-700',
  },
  info: {
    bg: 'bg-info-50',
    iconColor: 'text-info-500',
    text: 'text-info-700',
  },
  success: {
    bg: 'bg-success-50',
    iconColor: 'text-success-500',
    text: 'text-success-700',
  },
  neutral: {
    bg: 'bg-neutral-50',
    iconColor: 'text-neutral-500',
    text: 'text-neutral-700',
  },
};

const icons = {
  error: ExclamationCircleIcon,
  warning: ExclamationTriangleIcon,
  info: InformationCircleIcon,
  success: CheckCircleIcon,
  neutral: null,
};

type Props = {
  fullWidth?: boolean;
  title?: string | null;
  children?: ReactNode;
  severity: Severity;
  withoutIcon?: boolean;
  loading?: boolean;
  testId?: string;
  className?: string;
  actions?: {
    alignment?: 'right' | 'bottom-left' | 'bottom-right'; //default's to 'right'
    primary: Omit<Parameters<typeof Button>[0], 'size' | 'className'>;
    secondary?: Omit<Parameters<typeof Button>[0], 'size' | 'variant' | 'className'>;
  };
};

export const Alert = ({
  title,
  children,
  className,
  severity,
  withoutIcon,
  loading,
  testId,
  actions,
  fullWidth,
  ref,
}: WithRef<Props, HTMLDivElement>) => {
  const Icon = withoutIcon ? null : icons[severity];
  // `actions.alignment` "default's to 'right'" per its own type comment, but is only ever set to
  // the literal string when a caller passes it explicitly — most callers rely on the default and
  // leave it `undefined`. Normalize it once so every alignment-dependent check below (including
  // the icon's vertical position) treats an omitted alignment the same as an explicit 'right'.
  const alignment = actions?.alignment ?? 'right';

  return (
    <div
      data-testid={testId}
      data-severity={severity}
      className={cn('w-full', classes[severity].bg, fullWidth ? 'px-8 py-4' : 'rounded-md p-4', className)}
      ref={ref}
    >
      <div className="flex gap-3">
        {Icon || loading ? (
          <div
            className={cn('shrink-0 self-start', {
              'pt-0.5': Boolean(title),
              // Only nudges the icon when `actions` makes the content row taller than the icon
              // itself (e.g. a button row) — a titleless alert with no actions has nothing to
              // vertically center against, so it's left as-is.
              'pt-1': Boolean(actions && !title && alignment === 'right'),
            })}
          >
            {loading ? (
              <LoadingSpinner variant={severity} sizeClassName="size-6" />
            ) : (
              Icon && <Icon className={cn('size-5 stroke-2', classes[severity].iconColor)} aria-hidden="true" />
            )}
          </div>
        ) : null}
        <div
          className={cn(
            'flex w-full gap-2',
            alignment.includes('bottom') ? 'flex-col' : 'items-center justify-between',
          )}
        >
          <div className="w-full space-y-2">
            {!!title && <h3 className={cn('text-base font-bold', classes[severity].text)}>{title}</h3>}
            {!!children && <div className="flex flex-col gap-2 text-sm text-gray-900">{children}</div>}
          </div>
          {actions ? (
            <div
              className={cn(
                'flex gap-2',
                alignment === 'bottom-left' ? 'w-max flex-row-reverse justify-start' : 'justify-end',
              )}
            >
              {actions.secondary && <Button {...actions.secondary} variant="secondary" size="small" />}
              {actions.primary && (
                <Button size="small" variant={actions.secondary ? 'primary' : 'secondary'} {...actions.primary} />
              )}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};
